import { useEffect, useState, useCallback } from 'react';
import { trackRender } from '../utils/debugRender';
import { analyticsApi } from '../services/api';
import toast from 'react-hot-toast';
import { Tabs, TabItem, Card, Button, ButtonGroup, Badge, Spinner } from 'flowbite-react';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  CalendarIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  UserGroupIcon,
  BellAlertIcon
} from '@heroicons/react/24/outline';
import EChart from '../components/Charts/EChart';
import SubscriptionsTab from './Analytics/SubscriptionsTab';
import BehaviorTab from './Analytics/BehaviorTab';
import RetentionTab from './Analytics/RetentionTab';
import ErrorsTab from './Analytics/ErrorsTab';

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState('30d');
  const [activeTab, setActiveTab] = useState(0);

  // 🔍 DEBUG: Track renders to identify infinite loops
  trackRender('Analytics', { loading, activeTab, dateRange });

  // Real data from API
  const [timelineData, setTimelineData] = useState(null);
  const [entryPatternsData, setEntryPatternsData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);

  useEffect(() => {
    loadStats();
  }, [dateRange]);

  const getDaysFromRange = () => {
    return dateRange === '7d' ? 7 : dateRange === '30d' ? 30 : 90;
  };

  async function loadStats() {
    try {
      setLoading(true);
      const days = getDaysFromRange();

      const [overview, timeline, entryPatterns, subscriptions] = await Promise.all([
        analyticsApi.overview(),
        analyticsApi.subscriptionTimeline(days),
        analyticsApi.entryPatterns(days),
        analyticsApi.subscriptions()
      ]);

      setStats(overview);
      setTimelineData(timeline);
      setEntryPatternsData(entryPatterns);
      setSubscriptionData(subscriptions);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    await loadStats();
    setRefreshing(false);
    toast.success('Analytics refreshed');
  }

  // 🔍 DEBUG: Memoize Tabs callback to prevent infinite loops
  const handleTabChange = useCallback((tab) => {
    console.log('🔄 Analytics tab changed to:', tab);
    setActiveTab(tab);
  }, []);

  const handleExport = (format, tabName) => {
    toast.success(`Exporting ${tabName} to ${format.toUpperCase()}...`);
    // TODO: Implement actual export functionality
  };

  // 🔍 FIX: Create memoized export handlers for each tab to prevent inline function recreation
  const handleExportOverview = useCallback((format) => handleExport(format, 'Overview'), []);
  const handleExportSubscriptions = useCallback((format) => handleExport(format, 'Subscriptions'), []);
  const handleExportBehavior = useCallback((format) => handleExport(format, 'User Behavior'), []);
  const handleExportRetention = useCallback((format) => handleExport(format, 'Retention'), []);
  const handleExportErrors = useCallback((format) => handleExport(format, 'Errors'), []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Spinner size="xl" />
        <p className="mt-4 text-gray-600">Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">Usage statistics and performance metrics</p>
        </div>

        <div className="flex items-center gap-3">
          {/* Date Range Selector */}
          <div className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white p-1 dark:border-gray-600 dark:bg-gray-800">
            <CalendarIcon className="ml-2 h-5 w-5 text-gray-500" />
            <ButtonGroup>
              <Button
                color={dateRange === '7d' ? 'blue' : 'gray'}
                size="sm"
                onClick={() => setDateRange('7d')}
              >
                7 days
              </Button>
              <Button
                color={dateRange === '30d' ? 'blue' : 'gray'}
                size="sm"
                onClick={() => setDateRange('30d')}
              >
                30 days
              </Button>
              <Button
                color={dateRange === '90d' ? 'blue' : 'gray'}
                size="sm"
                onClick={() => setDateRange('90d')}
              >
                90 days
              </Button>
            </ButtonGroup>
          </div>

          {/* Refresh Button */}
          <Button
            color="blue"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Card>
        <Tabs
          aria-label="Analytics tabs"
          variant="underline"
          onActiveTabChange={handleTabChange}
        >
          {/* Overview Tab */}
          <TabItem
            title="Overview"
            icon={ChartBarIcon}
          >
            <div className="space-y-6 pt-4">
              <div className="flex justify-end">
                <ExportButtons
                  onExport={handleExportOverview}
                />
              </div>
              <OverviewTab
                stats={stats}
                timelineData={timelineData}
                entryPatternsData={entryPatternsData}
                dateRange={dateRange}
                loading={loading}
              />
            </div>
          </TabItem>

          {/* Subscriptions Tab */}
          <TabItem
            title="Subscriptions"
            icon={CurrencyDollarIcon}
          >
            <div className="space-y-6 pt-4">
              <div className="flex justify-end">
                <ExportButtons
                  onExport={handleExportSubscriptions}
                />
              </div>
              <SubscriptionsTab dateRange={dateRange} />
            </div>
          </TabItem>

          {/* User Behavior Tab */}
          <TabItem
            title="User Behavior"
            icon={UserGroupIcon}
          >
            <div className="space-y-6 pt-4">
              <div className="flex justify-end">
                <ExportButtons
                  onExport={handleExportBehavior}
                />
              </div>
              <BehaviorTab dateRange={dateRange} />
            </div>
          </TabItem>

          {/* Retention Tab */}
          <TabItem
            title="Retention"
            icon={UserGroupIcon}
          >
            <div className="space-y-6 pt-4">
              <div className="flex justify-end">
                <ExportButtons
                  onExport={handleExportRetention}
                />
              </div>
              <RetentionTab />
            </div>
          </TabItem>

          {/* Errors Tab */}
          <TabItem
            title="Errors"
            icon={BellAlertIcon}
          >
            <div className="space-y-6 pt-4">
              <div className="flex justify-end">
                <ExportButtons
                  onExport={handleExportErrors}
                />
              </div>
              <ErrorsTab dateRange={dateRange} />
            </div>
          </TabItem>
        </Tabs>
      </Card>
    </div>
  );
}

// Export Buttons Component
function ExportButtons({ onExport }) {
  return (
    <ButtonGroup>
      <Button
        size="sm"
        color="gray"
        onClick={() => onExport('csv')}
      >
        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
        CSV
      </Button>
      <Button
        size="sm"
        color="gray"
        onClick={() => onExport('pdf')}
      >
        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
        PDF
      </Button>
    </ButtonGroup>
  );
}

// Overview Tab Component
function OverviewTab({ stats, timelineData, entryPatternsData, dateRange, loading }) {
  if (loading) {
    return <LoadingSkeleton />;
  }

  if (!stats) {
    return <EmptyState message="No analytics data available" />;
  }

  const userGrowthData = transformTimelineData(timelineData);
  const entryPatternsChartData = transformEntryPatternsData(entryPatternsData);
  const conversionData = generateConversionData(stats);

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          title="Total Users"
          value={stats?.users?.total || 0}
          subtitle="All registered users"
          icon="👥"
          trend={12.5}
        />
        <MetricCard
          title="Active Users"
          value={stats?.users?.active30d || 0}
          subtitle={`Active in last ${dateRange}`}
          icon="✓"
          trend={8.3}
        />
        <MetricCard
          title="Premium Users"
          value={stats?.users?.premium || 0}
          subtitle="Users on premium plan"
          icon="⭐"
          trend={15.2}
        />
        <MetricCard
          title="Conversion Rate"
          value={`${((stats?.users?.premium / stats?.users?.total) * 100 || 0).toFixed(1)}%`}
          subtitle="Free to premium"
          icon="📈"
          trend={3.7}
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Subscription Timeline
          </h2>
          {timelineData?.timeline?.length > 0 ? (
            <EChart option={userGrowthData} height="300px" />
          ) : (
            <EmptyState message="No subscription timeline data" />
          )}
        </Card>

        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Entry Patterns by Hour
          </h2>
          {entryPatternsData?.by_hour?.length > 0 ? (
            <EChart option={entryPatternsChartData} height="300px" />
          ) : (
            <EmptyState message="No entry patterns data" />
          )}
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            Conversion Metrics
          </h2>
          <EChart option={conversionData} height="300px" />
        </Card>

        <Card>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            User Engagement
          </h2>
          <div className="space-y-4">
            <ProgressBar
              label="Active Users"
              value={stats?.users?.active30d || 0}
              max={stats?.users?.total || 1}
              color="green"
            />
            <ProgressBar
              label="Premium Users"
              value={stats?.users?.premium || 0}
              max={stats?.users?.total || 1}
              color="yellow"
            />
            <ProgressBar
              label="Profile Completion"
              value={stats?.users?.withProfile || 0}
              max={stats?.users?.total || 1}
              color="blue"
            />
          </div>
          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Quick Stats
            </h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-600 dark:text-gray-400">Total Data Entries</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">
                  {(stats?.data?.totalEntries || 0).toLocaleString()}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-600 dark:text-gray-400">Avg. per User</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">
                  {Math.round((stats?.data?.totalEntries || 0) / (stats?.users?.total || 1))}
                </dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-600 dark:text-gray-400">Database Size</dt>
                <dd className="font-semibold text-gray-900 dark:text-white">
                  {stats?.database?.size || 'N/A'}
                </dd>
              </div>
            </dl>
          </div>
        </Card>
      </div>

      {/* System Health */}
      <Card>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
          System Health
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatusRow label="Database" status="healthy" uptime="99.9%" />
          <StatusRow label="API Worker" status="healthy" uptime="99.8%" />
          <StatusRow label="Authentication" status="healthy" uptime="100%" />
        </div>
      </Card>
    </div>
  );
}

// Loading Skeleton Component
function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      {/* Stats Skeleton */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        ))}
      </div>
      {/* Charts Skeleton */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {[...Array(2)].map((_, i) => (
          <div key={i} className="h-80 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
        ))}
      </div>
    </div>
  );
}

// Empty State Component
function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <ChartBarIcon className="h-16 w-16 text-gray-400 mb-4" />
      <p className="text-gray-600 dark:text-gray-400 text-lg">{message}</p>
      <p className="text-gray-500 dark:text-gray-500 text-sm mt-2">
        Try adjusting the date range or check back later
      </p>
    </div>
  );
}

// Transform subscription timeline data from API to ECharts format
function transformTimelineData(data) {
  if (!data?.timeline || data.timeline.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Subscriptions' },
      series: []
    };
  }

  const categories = data.timeline.map(item => {
    const date = new Date(item.date);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  });

  const newSubs = data.timeline.map(item => item.new_subscriptions || 0);
  const cancellations = data.timeline.map(item => Math.abs(item.cancellations || 0));

  return {
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'cross'
      }
    },
    legend: {
      data: ['New Subscriptions', 'Cancellations'],
      top: 0
    },
    xAxis: {
      type: 'category',
      data: categories,
      boundaryGap: false,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Subscriptions',
      splitLine: {
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: 'New Subscriptions',
        type: 'line',
        data: newSubs,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(16, 185, 129, 0.4)' },
              { offset: 1, color: 'rgba(16, 185, 129, 0.1)' }
            ]
          }
        },
        itemStyle: {
          color: '#10b981'
        },
        lineStyle: {
          width: 2
        }
      },
      {
        name: 'Cancellations',
        type: 'line',
        data: cancellations,
        smooth: true,
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(239, 68, 68, 0.4)' },
              { offset: 1, color: 'rgba(239, 68, 68, 0.1)' }
            ]
          }
        },
        itemStyle: {
          color: '#ef4444'
        },
        lineStyle: {
          width: 2
        }
      }
    ]
  };
}

// Transform entry patterns data from API to ECharts format
function transformEntryPatternsData(data) {
  if (!data?.by_hour || data.by_hour.length === 0) {
    return {
      xAxis: { type: 'category', data: [] },
      yAxis: { type: 'value', name: 'Entries' },
      series: []
    };
  }

  const sortedData = [...data.by_hour].sort((a, b) => a.hour - b.hour);

  const categories = sortedData.map(item => {
    const hour = item.hour;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}${ampm}`;
  });

  const entryData = sortedData.map(item => item.entry_count || 0);

  return {
    tooltip: {
      trigger: 'axis',
      formatter: (params) => {
        const param = params[0];
        return `${param.name}<br/>${param.marker}${param.seriesName}: ${param.value.toLocaleString()} entries`;
      }
    },
    xAxis: {
      type: 'category',
      data: categories,
      name: 'Hour of Day',
      nameLocation: 'middle',
      nameGap: 30,
      axisLabel: {
        rotate: 45,
        fontSize: 11
      }
    },
    yAxis: {
      type: 'value',
      name: 'Number of Entries',
      splitLine: {
        lineStyle: {
          type: 'dashed'
        }
      }
    },
    series: [
      {
        name: 'Entries',
        type: 'bar',
        data: entryData,
        itemStyle: {
          color: '#3b82f6',
          borderRadius: [4, 4, 0, 0]
        },
        barWidth: '70%',
        emphasis: {
          itemStyle: {
            color: '#2563eb'
          }
        }
      }
    ]
  };
}

// Generate conversion data for ECharts
function generateConversionData(stats) {
  const total = stats?.users?.total || 100;
  const premium = stats?.users?.premium || 15;
  const free = total - premium;

  return {
    tooltip: {
      trigger: 'item',
      formatter: '{a} <br/>{b}: {c} ({d}%)'
    },
    legend: {
      bottom: 10,
      left: 'center',
      data: ['Free Users', 'Premium Users']
    },
    series: [
      {
        name: 'Users',
        type: 'pie',
        radius: ['50%', '70%'],
        center: ['50%', '45%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          formatter: '{d}%',
          fontSize: 12,
          fontWeight: 'bold'
        },
        emphasis: {
          label: {
            show: true,
            fontSize: 14,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          show: true
        },
        data: [
          { value: free, name: 'Free Users', itemStyle: { color: '#3b82f6' } },
          { value: premium, name: 'Premium Users', itemStyle: { color: '#eab308' } }
        ]
      }
    ]
  };
}

function MetricCard({ title, value, subtitle, icon, trend }) {
  const isPositive = trend >= 0;

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      <div className="flex items-center gap-2 mt-2">
        <p className="text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
        {trend !== undefined && (
          <Badge color={isPositive ? 'success' : 'failure'} size="sm">
            {isPositive ? '↑' : '↓'} {Math.abs(trend)}%
          </Badge>
        )}
      </div>
    </Card>
  );
}

function ProgressBar({ label, value, max, color }) {
  const percentage = (value / max) * 100;
  const colorClasses = {
    green: 'bg-green-500',
    yellow: 'bg-yellow-500',
    blue: 'bg-blue-500'
  };

  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="font-medium text-gray-900 dark:text-white">
          {value.toLocaleString()} / {max.toLocaleString()} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${colorClasses[color]} transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

function StatusRow({ label, status, uptime }) {
  const statusConfig = {
    healthy: { color: 'success', text: 'Healthy' },
    warning: { color: 'warning', text: 'Warning' },
    error: { color: 'failure', text: 'Error' }
  };

  const config = statusConfig[status];

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
        <Badge color={config.color} size="sm">{config.text}</Badge>
      </div>
      <p className="text-xs text-gray-500 dark:text-gray-400">Uptime: {uptime}</p>
    </div>
  );
}
